import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
} from '@chakra-ui/react';
import { deleteUploadImage } from 'api/deleteUploadImage';
import { GET_USER_MEDIA_API } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { memo, useRef } from 'react';
import { useSWRConfig } from 'swr';

// mediaId is the `media_id` field in user_media_libraray table, format is `id.extension`, it's used by the deleteUploadImageAlertDialog to identify which file to delete when we call the deleteUploadImage api
// OnResetId is the function to reset mediaId to an empty string when an upload image is deleted or delete dialog is closed
interface DeleteUploadImageAlertProps {
  mediaId: string;
  isOpen: boolean;
  onClose: () => void;
  onResetId: () => void;
}

interface DeleteUploadErrorResponse {
  message: string;
}

type ShowDeleteErrorToast = (title: string, description: string) => void;

const DeleteUploadImageAlertDialog = memo(({ mediaId, isOpen, onClose, onResetId }: DeleteUploadImageAlertProps) => {
  const { mutate } = useSWRConfig();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const toast = useToast();

  const showDeleteErrorToast: ShowDeleteErrorToast = (title, description) => {
    toast({
      title,
      description,
      status: 'error',
    });
  };

  const onDelete = async () => {
    if (!!mediaId) {
      try {
        await deleteUploadImage(mediaId);
        mutate(GET_USER_MEDIA_API);
      } catch (error) {
        const deleteUploadErr = error as DeleteUploadErrorResponse;
        console.error(deleteUploadErr.message);
        showDeleteErrorToast('Delete Failed', deleteUploadErr.message);
      } finally {
        onResetId();
        onClose();
      }
    }
  };

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay bg='blackAlpha.500'>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold'>
            Delete Image
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text as='span'>Are you sure you want to remove this uploaded image from the library?</Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={onDelete} ml={3}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
});
DeleteUploadImageAlertDialog.displayName = 'DeleteUploadImageAlertDialog';

export default DeleteUploadImageAlertDialog;
